import { Markdown } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import React, { startTransition, use, useEffect, useRef, useState } from 'react';

import { WebsiteLink } from 'src/components/common/website-link';
import ArrowRightThick from 'src/components/icons/arrow-right-thick';
import ArrowRightThin from 'src/components/icons/arrow-right-thin';
import HeaderDesktopLevelTwoMenu from 'src/components/layout/redesign/header-desktop/header-level-two-menu';
import { SiteContext } from 'src/context/site-context';
import { amplitudeBrandName, amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { onMarkdownLinkClick } from 'src/helpers/markdown-helper';
import { pathToTitle } from 'src/helpers/string-helper';
import { calculateDesktopGridWidth } from 'src/styling/constants';

import * as styles from './header-menu.module.scss';

type HeaderDesktopSubMenuProps = Queries.HeaderLevelOneMenuItem & {
  overviewText: string;
  visible: boolean;
  children?: React.ReactNode;
  enableOverflow?: boolean;
  onFinish?: () => void;
};

const HeaderDesktopMenu = (props: HeaderDesktopSubMenuProps) => {
  const { styling, pageLevel, buildType, isMeridian, isHarbour, language } = use(SiteContext);
  const menuStyle = {
    '--overview-width': calculateDesktopGridWidth(10, isMeridian ? 2.5 : 4),
    '--level-two-width': calculateDesktopGridWidth(10, isMeridian ? 3.25 : 3),
  } as React.CSSProperties;
  const ref = useRef<HTMLDivElement | null>(null);
  const level3Refs = useRef<Record<number, HTMLElement | null>>({});

  const [displayedSubMenuIndex, setDisplayedSubMenuIndex] = useState<number | null>(null);
  const [componentHeight, setHeight] = useState(0);
  const [displayedLevel3ComponentHeight, setDisplayedLevel3ComponentHeight] = useState(0);

  const [prevVisible, setPrevVisible] = useState(props.visible);
  if (props.visible !== prevVisible) {
    setPrevVisible(props.visible);
    if (!props.visible) {
      setDisplayedSubMenuIndex(null);
    }
  }

  useEffect(() => {
    setDisplayedLevel3ComponentHeight(
      level3Refs.current[displayedSubMenuIndex!]?.getBoundingClientRect()?.height || 0
    );
  }, [displayedSubMenuIndex]);

  useEffect(() => {
    if (!ref.current || !props.visible) {
      return;
    }

    const updateHeaderHeightVariable = () => {
      const containerHeight = ref.current?.getBoundingClientRect().height;

      if (!containerHeight) {
        return;
      }
      setHeight(containerHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      startTransition(updateHeaderHeightVariable);
    });

    resizeObserver.observe(ref.current);
    updateHeaderHeightVariable();

    return () => resizeObserver.disconnect();
  }, [props.visible]);

  const handleClick = () => {
    props.onFinish?.();
    void amplitudeTrackEvent(`Click to Header Desktop Menu`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      linkName: capitalize(props.overviewText || ''),
      linkPath: pathToTitle(props.link || ''),
      component: 'Header Menu',
    });
  };

  const shouldShowMenu =
    !!props.children || !!props.description?.trim() || !!props.levelTwoMenuItems?.length;

  if (!shouldShowMenu) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.container,
        props.visible
          ? classNames(
              styles.menuVisible,
              props.enableOverflow ? styles.menuVisibleOverflow : styles.menuVisibleNoOverflow
            )
          : styles.menuHidden
      )}
      style={props.visible ? { ...menuStyle, height: componentHeight } : menuStyle}
      tabIndex={-1}
    >
      <div className={styles.wrapper} ref={ref}>
        <div
          className={styles.overviewWrapper}
          style={{ minHeight: displayedLevel3ComponentHeight }}
        >
          <div>
            <div
              className={classNames(
                styles.title,
                isMeridian ? styles.titleRegular : styles.titleLight
              )}
            >
              {props.name}
            </div>
            {!!props.description?.trim() && (
              <Markdown
                markdownText={props.description}
                colorOverride={isMeridian ? styling.colors.meridianBlue : styling.colors.capeCod}
                brand={getBrandForBuildType(buildType)}
                onLinkClick={onMarkdownLinkClick(buildType, language, 'Desktop Header Menu')}
                classNameToOverride={styles.menuItem}
              />
            )}
            {!!props.link && (
              <WebsiteLink
                to={props.link}
                handleClick={handleClick}
                className={classNames(styles.overviewLink, styles.hover)}
              >
                <div className={styles.text}>{props.overviewText}</div>
                {isMeridian ? (
                  <ArrowRightThick height="0.8125rem" fill={styling.colors.meridianBlue} />
                ) : (
                  <ArrowRightThin
                    size={16}
                    fill={isHarbour ? styling.colors.harbourGold : styling.colors.capeCod}
                  />
                )}
              </WebsiteLink>
            )}
          </div>
          {isMeridian && <div className={styles.divider} />}
        </div>
        {!!props.levelTwoMenuItems?.length && (
          <ul className={styles.levelTwoWrapper}>
            {props.levelTwoMenuItems.map((levelTwoMenu, index) => {
              const hasLevelThreeItems = !!levelTwoMenu.levelThreeMenuItems?.length;
              return (
                <li key={levelTwoMenu.name}>
                  <HeaderDesktopLevelTwoMenu
                    selected={displayedSubMenuIndex === index}
                    levelTwoMenu={levelTwoMenu}
                    onSelectMenu={() => setDisplayedSubMenuIndex(hasLevelThreeItems ? index : null)}
                    updateRef={(ref) => (level3Refs.current[index] = ref)}
                    onFinish={props.onFinish}
                    menuPosition={index + 1}
                    totalLevelTwoItems={props.levelTwoMenuItems!.length}
                  />
                </li>
              );
            })}
          </ul>
        )}
        {props.children}
      </div>
    </div>
  );
};

export default HeaderDesktopMenu;
